import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission, usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { Vehicle, VehicleStatus, VehicleTemplate, VehicleWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { VEHICLE_STATUS } from '@klareospace/pharos-lib-types';
import { deduplicate } from '@klareospace/pharos-lib-utils';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SideDrawer,
  VehicleProfileIcon,
} from '@klareospace/pharos-lib-web-utils';

import { useCarrier, useCarriers } from '../../carriers/hooks';
import { withAssignedOption } from '../../shared/scope-filters';
import { useSites } from '../../sites/hooks';
import { buildAvailableDriversFilter, carrierSiteQueryIds, pruneInvalidSelection } from './vehicle-drawer.utils';
import { VehicleTemplateDrawer } from '../../vehicle-templates/components';
import { useVehicleTemplates } from '../../vehicle-templates/hooks';
import { useAvailableDrivers, useVehicleForm } from '../hooks';
import type { VehicleFormInput } from '../schemas/vehicle.schema';
import type { VehicleScope } from '../types/vehicle.types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VehicleDrawerProps {
  carrierId?: string;
  onClose: () => void;
  open: boolean;
  scope: VehicleScope;
  siteId?: string;
  spaceId?: string;
  vehicle: Vehicle | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const NO_DRIVER_VALUE = '__no-driver__';

const CARRIERS_PAGE_SIZE = 100;
const SITES_PAGE_SIZE = 100;
const TEMPLATES_PAGE_SIZE = 100;
const VEHICLE_STATUSES: { label: string; value: VehicleStatus }[] = [
  { label: VEHICLE_STATUS.AVAILABLE, value: VEHICLE_STATUS.AVAILABLE },
  { label: VEHICLE_STATUS.IN_MAINTENANCE, value: VEHICLE_STATUS.IN_MAINTENANCE },
];

// ─── Component ────────────────────────────────────────────────────────────────

// eslint-disable-next-line complexity
export function VehicleDrawer({
  carrierId,
  onClose,
  open,
  scope,
  siteId,
  spaceId,
  vehicle,
}: VehicleDrawerProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const isEditMode = vehicle !== null;

  const defaultValues: Partial<VehicleFormInput> = {
    carrierId: carrierId ?? '',
    siteIds: siteId ? [siteId] : [],
    spaceId: spaceId ?? '',
  };

  const { form, isPending, onSubmit } = useVehicleForm(vehicle, onClose, defaultValues);
  const { hasPermission } = usePermissions();
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [createdTemplate, setCreatedTemplate] = useState<VehicleTemplate | null>(null);
  const [pendingTemplateSelection, setPendingTemplateSelection] = useState<string | null>(null);

  // A parent-initiated close (e.g. programmatic) must not leave the nested template drawer open
  // over a fresh form on reopen.
  useEffect(() => {
    if (!open) setTemplateDrawerOpen(false);
  }, [open]);

  const selectedCarrierId = form.watch('carrierId');
  const selectedSiteIds: string[] = form.watch('siteIds') ?? [];

  // Site scope: only carriers enabled on the locked site may own vehicles there, so filter the
  // dropdown accordingly (the carrier service accepts one scope filter at a time, hence no
  // spaceId alongside). Space scope keeps the space-wide list; carrier scope keeps all carriers
  // (the select is disabled and only needs the locked carrier's label).
  const { data: carriersData } = useCarriers({
    enabledOnSiteId: scope === 'site' ? siteId : undefined,
    page: 1,
    pageSize: CARRIERS_PAGE_SIZE,
    spaceId: scope === 'space' ? spaceId : undefined,
  });

  // The vehicle's existing carrier may no longer be enabled on the locked site (pre-existing
  // data drift) — fetch it explicitly so it stays visible and selectable in edit mode
  // (mirrors the driver drawer's assigned-carrier escape hatch).
  const { data: assignedCarrier } = useCarrier(vehicle?.carrierId || undefined);

  const { data: templatesData } = useVehicleTemplates({
    limit: TEMPLATES_PAGE_SIZE,
    page: 1,
    statuses: ['active'],
  });

  // The vehicle's carrier may have drifted outside the locked site's enabled carriers, so it can be
  // absent from carriersData (site scope). Fall back to the assigned-carrier escape hatch so site
  // filtering still resolves from its enabledScopes instead of collapsing to "all sites".
  const selectedCarrier = useMemo(
    () =>
      (carriersData?.items ?? []).find((c) => c.id === selectedCarrierId) ??
      (assignedCarrier?.id === selectedCarrierId ? assignedCarrier : undefined),
    [carriersData, selectedCarrierId, assignedCarrier],
  );

  // The vehicle's persisted sites are preserved through the carrier filter (edit-mode drift): they
  // stay selected and are fetched for their labels, but are not offered as new dropdown options.
  const persistedSiteIds = useMemo(() => vehicle?.siteIds?.map(String) ?? [], [vehicle]);

  // Site context for the default-driver list: the vehicle's selected sites when set, otherwise the
  // carrier's enabled sites (allowlist carriers → those ids; all-sites carriers → null → space-wide).
  // Without this fallback, carrier-settings (no site field) would widen to the whole space and offer
  // drivers scoped to sites where the carrier isn't enabled.
  const driverSiteIds =
    selectedSiteIds.length > 0 ? selectedSiteIds : (carrierSiteQueryIds(selectedCarrier, spaceId ?? null) ?? []);

  // Default-driver list: restricted to the vehicle's carrier AND its site context. Always scoped to
  // the space so a multi-space carrier can't leak drivers from other spaces.
  // Gated on `open` so it does not fire while the drawer is closed.
  const { data: driversData } = useAvailableDrivers(
    buildAvailableDriversFilter({
      canList: hasPermission('vehicles:list'),
      carrierId: selectedCarrierId,
      excludeVehicleId: vehicle?.id,
      open,
      siteIds: driverSiteIds,
      spaceId: spaceId ?? null,
    }),
  );

  // The vehicle's existing carrier may no longer be enabled on the locked site (data drift) — the
  // assigned-carrier escape hatch keeps it selectable in edit mode.
  const carrierOptions = useMemo(
    () =>
      withAssignedOption(
        (carriersData?.items ?? []).map((c) => ({ label: c.name, value: c.id })),
        assignedCarrier,
        (c) => ({ label: c.name, value: c.id }),
      ),
    [carriersData, assignedCarrier],
  );

  // A just-created template may not yet appear in templatesData (invalidation refetch race, or the
  // page-1 list is full) — the escape hatch keeps it selectable, mirroring the assigned-carrier/
  // assigned-driver pattern above.
  const templateOptions = useMemo(
    () =>
      withAssignedOption(
        (templatesData?.items ?? []).map((tmpl) => ({ label: tmpl.name, profile: tmpl.profile, value: tmpl.id })),
        createdTemplate,
        (tmpl) => ({ label: tmpl.name, profile: tmpl.profile, value: tmpl.id }),
      ),
    [templatesData, createdTemplate],
  );

  // Radix's Select only registers a SelectItem into its native-options collection via a layout
  // effect once it has actually rendered — setting the controlled value in the same tick as adding
  // the option races that registration and gets silently reverted. Deferring the selection to a
  // follow-up effect lets the option mount (and register) first.
  useEffect(() => {
    if (!pendingTemplateSelection) return;
    if (!templateOptions.some((opt) => opt.value === pendingTemplateSelection)) return;
    form.setValue('vehicleTemplateId', pendingTemplateSelection, { shouldDirty: true, shouldValidate: true });
    setPendingTemplateSelection(null);
  }, [pendingTemplateSelection, templateOptions, form]);

  // Derive which site IDs to query based on the selected carrier's enabledScopes.
  // When the carrier uses an allowlist (enabledOnAllSites: false), we fetch only those sites.
  // When it's enabledOnAllSites: true, we fetch all and filter disabled ones client-side.
  const siteQueryIds = useMemo((): string[] | null => {
    if (!selectedCarrier || !spaceId) return null;
    const carrierScope = selectedCarrier.enabledScopes.find((s) => s.spaceId === spaceId);
    if (!carrierScope) return [];
    if (carrierScope.enabledOnAllSites) return null;
    return (carrierScope.siteSpecifics ?? []).filter((s) => !s.disabled).map((s) => s.siteId);
  }, [selectedCarrier, spaceId]);

  // Fetch the carrier's enabled sites plus the vehicle's persisted (possibly drifted) sites, so the
  // latter resolve to a label for the preserved-selection badge without being offered for re-add.
  const siteFetchIds = useMemo(
    () => (siteQueryIds === null ? undefined : deduplicate([...siteQueryIds, ...persistedSiteIds])),
    [siteQueryIds, persistedSiteIds],
  );

  const { data: sitesData } = useSites({
    enabled: !!spaceId && (siteFetchIds === undefined || siteFetchIds.length > 0),
    ids: siteFetchIds,
    page: 1,
    pageSize: SITES_PAGE_SIZE,
    spaceIds: spaceId ? [spaceId] : undefined,
  });

  const siteOptions = useMemo(() => (sitesData?.items ?? []).map((s) => ({ label: s.name, value: s.id })), [sitesData]);

  const enabledSiteIds = useMemo((): string[] | null => {
    if (!selectedCarrier || !spaceId) return null;
    const carrierScope = selectedCarrier.enabledScopes.find((s) => s.spaceId === spaceId);
    if (!carrierScope) return [];
    if (carrierScope.enabledOnAllSites) {
      const disabledIds = new Set((carrierScope.siteSpecifics ?? []).filter((s) => s.disabled).map((s) => s.siteId));
      return disabledIds.size > 0 ? siteOptions.map((o) => o.value).filter((id) => !disabledIds.has(id)) : null;
    }
    return (carrierScope.siteSpecifics ?? []).filter((s) => !s.disabled).map((s) => s.siteId);
  }, [selectedCarrier, spaceId, siteOptions]);

  const filteredSiteOptions = useMemo(
    () => (enabledSiteIds === null ? siteOptions : siteOptions.filter((opt) => enabledSiteIds.includes(opt.value))),
    [enabledSiteIds, siteOptions],
  );

  // Persisted sites that fall outside the carrier's enabled set: shown as preserved (removable)
  // selections in the trigger, but not offered as re-selectable dropdown options.
  const driftedSiteOptions = useMemo(
    () =>
      siteOptions.filter(
        (opt) => persistedSiteIds.includes(opt.value) && !filteredSiteOptions.some((f) => f.value === opt.value),
      ),
    [siteOptions, persistedSiteIds, filteredSiteOptions],
  );

  // Prune sites that are no longer valid for the selected carrier. While the carrier is unchanged,
  // the vehicle's persisted sites are exempt so editing never silently drops them. Once the user
  // changes the carrier, the exemption lifts so unserved sites are pruned. Dirties the form only on
  // a real change.
  useEffect(() => {
    if (!selectedCarrierId || enabledSiteIds === null) return;
    const exemptSiteIds = selectedCarrierId === vehicle?.carrierId ? persistedSiteIds : [];
    const currentSiteIds: string[] = form.getValues('siteIds');
    const { changed, next } = pruneInvalidSelection(currentSiteIds, enabledSiteIds, exemptSiteIds);
    if (changed) {
      form.setValue('siteIds', next, { shouldDirty: true });
    }
  }, [enabledSiteIds, form, selectedCarrierId, persistedSiteIds, vehicle]);

  // The vehicle's current default driver must stay selectable even if it falls outside the
  // carrier/site filter (e.g. a pre-existing cross-carrier or drifted assignment) — without this
  // escape hatch, editing the vehicle would drop a driver it already has. Sourced from the
  // resolved refs the list passes in.
  const assignedDriver = (vehicle as VehicleWithResolvedRefs | null)?.resolvedDefaultDriver;
  const driverOptions = useMemo(
    () =>
      withAssignedOption(
        (driversData?.items ?? []).map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })),
        assignedDriver,
        (d) => ({ label: d.name, value: d.id }),
      ),
    [driversData, assignedDriver],
  );

  const title = isEditMode ? t('vehicles.drawer.editTitle') : t('vehicles.drawer.createTitle');
  const subtitle = isEditMode ? t('vehicles.drawer.editDescription') : t('vehicles.drawer.createDescription');

  function handleOpenChange(nextOpen: boolean): void {
    // A backdrop click on the nested template drawer's overlay must never also reset/close this
    // (parent) drawer — the nested drawer owns its own dismissal.
    if (templateDrawerOpen) return;
    if (!nextOpen) {
      form.reset();
      setCreatedTemplate(null);
      setPendingTemplateSelection(null);
      onClose();
    }
  }

  // Edit mode: only allow save once there is a valid change. Create mode: allow save (blocked while pending).
  // Mirrors the driver drawer so both edit flows behave consistently.
  const { isDirty, isValid } = form.formState;
  const isSubmitDisabled = isPending || (isEditMode ? !isDirty || !isValid : false);

  const footer = (
    <>
      <Button disabled={isSubmitDisabled} onClick={form.handleSubmit(onSubmit)} type="button">
        {isEditMode ? tCommon('actions.saveChanges') : t('vehicles.form.submit')}
      </Button>
      <Button onClick={(): void => handleOpenChange(false)} type="button" variant="outline">
        {tCommon('actions.cancel')}
      </Button>
    </>
  );

  return (
    <>
      <SideDrawer footer={footer} onOpenChange={handleOpenChange} open={open} subtitle={subtitle} title={title}>
        <Form {...form}>
          <form className="space-y-4">
            <FormField
              control={form.control}
              name="licensePlate"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('common.licensePlate')} <span>*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('vehicles.form.licensePlatePlaceholder')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('common.availability')} <span>*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VEHICLE_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {t(`vehicles.statuses.${s.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="carrierId"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {tCommon('fields.carrier')} <span>*</span>
                  </FormLabel>
                  <Select
                    disabled={(scope === 'carrier' && !isEditMode) || carrierOptions.length === 0}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('vehicles.form.carrierPlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {carrierOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="vehicleTemplateId"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('vehicles.form.vehicleTemplate')} <span>*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('vehicles.form.vehicleTemplatePlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {templateOptions.map((tmpl) => (
                        <SelectItem key={tmpl.value} value={tmpl.value}>
                          <span className="flex items-center gap-2">
                            <VehicleProfileIcon className="h-4 w-4" profile={tmpl.profile} />
                            {tmpl.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                  <RequirePermission permission="vehicle-templates:manage">
                    <Button
                      className="h-8 px-2 text-primary hover:text-primary"
                      onClick={(): void => setTemplateDrawerOpen(true)}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Plus className="me-1 h-4 w-4" />
                      {t('vehicles.form.createVehicleTemplate')}
                    </Button>
                  </RequirePermission>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultDriverId"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>{t('common.defaultDriver')}</FormLabel>
                  <Select
                    onValueChange={(val): void => field.onChange(val === NO_DRIVER_VALUE ? '' : val)}
                    value={field.value || NO_DRIVER_VALUE}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('vehicles.form.defaultDriverPlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_DRIVER_VALUE}>{t('vehicles.form.noDefaultDriver')}</SelectItem>
                      {driverOptions.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="siteIds"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>{tCommon('mainSelector.sites')}</FormLabel>
                  <FormControl>
                    <MultiSelect
                      className="border border-input bg-background shadow-none"
                      disabled={scope === 'site' && !isEditMode}
                      extraSelectedOptions={driftedSiteOptions}
                      hideClear
                      hideClose
                      maxCount={3}
                      onValueChange={field.onChange}
                      options={filteredSiteOptions}
                      placeholder={t('vehicles.form.sitesPlaceholder')}
                      searchable={false}
                      showLabelWhenFiltering
                      value={field.value ?? []}
                      variant="compact"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </SideDrawer>
      <VehicleTemplateDrawer
        onClose={(): void => setTemplateDrawerOpen(false)}
        onCreated={(tmpl): void => {
          setCreatedTemplate(tmpl);
          setPendingTemplateSelection(tmpl.id);
        }}
        open={templateDrawerOpen}
        vehicleTemplate={null}
      />
    </>
  );
}
